import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Coins, Gem, Bomb, Pickaxe, HandCoins, RotateCw } from "lucide-react";
import { useApp } from "../context/AppContext";
import { gamesApi } from "../lib/api";
import { inr } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useToast } from "../hooks/use-toast";
import { WinReveal } from "../components/WinReveal";
import { playCoinSplash, unlockAudio } from "../lib/rouletteSound";

const TILES = 25;
const MINE_PRESETS = [1, 3, 5, 10, 24];

// Risk-based mine colours — green (safe) → red (danger).
const MINE_COLORS = {
  1:  { grad: "linear-gradient(145deg,#4ade80,#22c55e 55%,#15803d)", glow: "34,197,94", text: "#052e16" },
  3:  { grad: "linear-gradient(145deg,#bef264,#84cc16 55%,#4d7c0f)", glow: "132,204,22", text: "#1a2e05" },
  5:  { grad: "linear-gradient(145deg,#fcd34d,#f59e0b 55%,#b45309)", glow: "245,158,11", text: "#1a1205" },
  10: { grad: "linear-gradient(145deg,#fdba74,#f97316 55%,#c2410c)", glow: "249,115,22", text: "#fff" },
  24: { grad: "linear-gradient(145deg,#fb7185,#ef4444 55%,#b91c1c)", glow: "244,63,94", text: "#fff" },
};

// Small "digging" click tone (WebAudio) for opening a safe gem tile.
function playDig() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const o = ctx.createOscillator(), g = ctx.createGain();
    o.type = "triangle";
    o.frequency.setValueAtTime(880, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(1400, ctx.currentTime + 0.08);
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.22);
    o.connect(g); g.connect(ctx.destination);
    o.start(); o.stop(ctx.currentTime + 0.24);
    setTimeout(() => ctx.close().catch(() => {}), 500);
  } catch { /* audio blocked */ }
}

function playBoom() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const dur = 0.6;
    const buf = ctx.createBuffer(1, ctx.sampleRate * dur, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / d.length, 2);
    const src = ctx.createBufferSource(); src.buffer = buf;
    const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 700;
    const g = ctx.createGain(); g.gain.setValueAtTime(0.5, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + dur);
    src.connect(lp); lp.connect(g); g.connect(ctx.destination);
    src.start();
    setTimeout(() => ctx.close().catch(() => {}), 800);
  } catch { /* audio blocked */ }
}

export default function GemMiner() {
  const { isAuthed, balance, refresh } = useApp();
  const { toast } = useToast();
  const [stake, setStake] = useState("100");
  const [mines, setMines] = useState(3);
  const [roundId, setRoundId] = useState(null);
  const [active, setActive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [gems, setGems] = useState(new Set());       // revealed safe tiles
  const [bombs, setBombs] = useState(new Set());      // revealed mine tiles (at game end)
  const [hitTile, setHitTile] = useState(null);       // the mine the player stepped on
  const [multiplier, setMultiplier] = useState(1);
  const [nextMult, setNextMult] = useState(null);
  const [outcome, setOutcome] = useState(null);       // null | "won" | "lost"
  const [reveal, setReveal] = useState(null);
  const [recent, setRecent] = useState([]);           // last cashout multipliers
  const streakRef = useRef(0);

  const roundStakeRef = useRef(0);

  if (!isAuthed) return (
    <div className="max-w-lg mx-auto px-4 py-24 text-center" data-testid="mines-signin-gate">
      <h2 className="font-display font-bold text-2xl">Sign in to play</h2>
      <Link to="/"><Button className="mt-6 font-semibold" data-testid="mines-go-home">Go Home</Button></Link>
    </div>
  );

  const resetBoard = () => {
    setGems(new Set());
    setBombs(new Set());
    setHitTile(null);
    setMultiplier(1);
    setNextMult(null);
    setOutcome(null);
  };

  const start = async () => {
    const s = Number(stake);
    if (!s || s < 20) { toast({ title: "Minimum bet is ₹20" }); return; }
    if (s > 100000) { toast({ title: "Higher bet limit crossed", description: "Please bet ₹100000 or below." }); return; }
    if (s > balance) { toast({ title: "Insufficient balance" }); return; }
    unlockAudio();
    setBusy(true);
    try {
      const res = await gamesApi.minesStart({ stake: s, mines });
      resetBoard();
      roundStakeRef.current = s;
      setRoundId(res.roundId);
      setActive(true);
      setMultiplier(res.multiplier);
      setNextMult(res.nextMultiplier);
      await refresh();
    } catch (e) {
      toast({ title: "Error", description: e?.response?.data?.detail || "Try again" });
    } finally {
      setBusy(false);
    }
  };

  const endWithLoss = (res) => {
    setBombs(new Set(res.minePositions || []));
    setHitTile(res.tile);
    setMultiplier(0);
    setActive(false);
    setOutcome("lost");
    streakRef.current = 0;
    playBoom();
  };

  const endWithWin = (mult, payout, minePositions) => {
    setBombs(new Set(minePositions || []));
    setActive(false);
    setOutcome("won");
    setMultiplier(mult);
    streakRef.current += 1;
    setRecent((prev) => [{ mult, won: true }, ...prev].slice(0, 12));
    playCoinSplash();
    setReveal({ label: `${mult}x`, won: true, payout, streak: streakRef.current });
  };

  const openTile = async (i) => {
    if (!active || busy || gems.has(i)) return;
    setBusy(true);
    try {
      const res = await gamesApi.minesReveal({ roundId, tile: i });
      if (res.hitMine) {
        endWithLoss(res);
      } else {
        playDig();
        setGems((prev) => new Set(prev).add(i));
        setMultiplier(res.multiplier);
        setNextMult(res.nextMultiplier ?? null);
        if (res.cleared) {
          endWithWin(res.multiplier, res.payout, res.minePositions);
          await refresh();
        }
      }
    } catch (e) {
      toast({ title: "Error", description: e?.response?.data?.detail || "Try again" });
    } finally {
      setBusy(false);
    }
    if (!active) await refresh();
  };

  const cashout = async () => {
    if (!active || busy || gems.size < 1) return;
    setBusy(true);
    try {
      const res = await gamesApi.minesCashout({ roundId });
      endWithWin(res.multiplier, res.payout, res.minePositions);
      await refresh();
    } catch (e) {
      toast({ title: "Error", description: e?.response?.data?.detail || "Try again" });
    } finally {
      setBusy(false);
    }
  };

  const potential = Math.round(roundStakeRef.current * multiplier);
  const gemsFound = gems.size;

  // Visual state for a tile: hidden | gem | bomb | dim (unopened after game over)
  const tileState = (i) => {
    if (gems.has(i)) return "gem";
    if (bombs.has(i)) return "bomb";
    if (outcome) return "dim";
    return "hidden";
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10 pb-28" data-testid="mines-page">
      <WinReveal open={!!reveal} {...(reveal || {})} onClose={() => setReveal(null)} />

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display font-bold text-3xl flex items-center gap-2">
            <Pickaxe className="h-6 w-6 text-primary" /> Gem Miner
          </h1>
          <p className="text-sm text-muted-foreground">Dig for gems · dodge the mines · cash out anytime</p>
        </div>
        <span className="rounded-full bg-secondary px-3 py-1.5 text-sm font-semibold" data-testid="mines-balance">
          <Coins className="h-4 w-4 inline mr-1 text-primary" />{inr(balance)}
        </span>
      </div>

      {/* Mine board — dark cavern with gold trim */}
      <div className="relative rounded-2xl p-5 sm:p-7 overflow-hidden"
        style={{
          background: "radial-gradient(ellipse 130% 95% at 50% 0%, #1b2430 0%, #0e141c 55%, #05080c 100%)",
          border: "1px solid rgba(217,181,69,0.55)",
          boxShadow: "inset 0 0 0 3px rgba(217,181,69,0.22), inset 0 0 70px rgba(0,0,0,0.55), 0 10px 30px rgba(0,0,0,0.5)",
        }}>
        {/* Multiplier + potential header */}
        <div className="relative flex items-center justify-between mb-4">
          <div className="rounded-xl bg-black/30 px-3 py-1.5 ring-1 ring-white/10" data-testid="mines-multiplier">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground block leading-none mb-0.5">Multiplier</span>
            <span className="font-display font-black text-xl text-yellow-300">{Number(multiplier).toFixed(2)}x</span>
          </div>
          <div className="text-center">
            <span className="inline-flex items-center gap-1 text-xs text-emerald-200/80">
              <Gem className="h-3.5 w-3.5 text-emerald-300" /> {gemsFound} found · {mines} mines
            </span>
          </div>
          <div className="rounded-xl bg-black/30 px-3 py-1.5 ring-1 ring-white/10 text-right">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground block leading-none mb-0.5">Cash value</span>
            <span className="font-display font-black text-xl text-primary" data-testid="mines-potential">{active || outcome === "won" ? inr(potential) : "—"}</span>
          </div>
        </div>

        {/* 5x5 grid */}
        <div className="relative grid grid-cols-5 gap-2 sm:gap-2.5" data-testid="mines-grid">
          {Array.from({ length: TILES }).map((_, i) => {
            const st = tileState(i);
            const isHit = hitTile === i;
            return (
              <button
                key={i}
                data-testid={`mines-tile-${i}`}
                onClick={() => openTile(i)}
                disabled={!active || busy || st !== "hidden"}
                className={`relative aspect-square rounded-xl grid place-items-center transition-[color,background-color,border-color,opacity,transform] duration-200 ${
                  st === "hidden" ? "hover:-translate-y-0.5 hover:brightness-125 active:scale-95 cursor-pointer" : "cursor-default"
                }`}
              >
                <TileFace state={st} isHit={isHit} />
              </button>
            );
          })}
        </div>

        {/* Status line */}
        <div className="relative mt-4 h-6 text-center text-sm">
          {outcome === "lost" && <span className="font-semibold text-red-400" data-testid="mines-status-lost">💥 Boom! You hit a mine.</span>}
          {outcome === "won" && <span className="font-semibold text-yellow-300" data-testid="mines-status-won">🏆 Cashed out {Number(multiplier).toFixed(2)}x · {inr(potential)}</span>}
          {active && (
            <span className="text-emerald-100/70">
              {nextMult ? <>Next gem pays <span className="text-yellow-300 font-semibold">{Number(nextMult).toFixed(2)}x</span></> : "Keep digging…"}
            </span>
          )}
          {!active && !outcome && <span className="text-muted-foreground">Set your bet & mines, then Start Digging</span>}
        </div>

        {/* Cash out button (only while a round is live) */}
        {active && (
          <Button
            className="relative mt-3 w-full h-12 font-extrabold text-base"
            onClick={cashout}
            disabled={busy || gems.size < 1}
            data-testid="mines-cashout-btn"
          >
            <HandCoins className="h-5 w-5 mr-1.5" />
            {gems.size < 1 ? "Open a tile to enable cash out" : `Cash Out ${inr(potential)}`}
          </Button>
        )}
      </div>

      {/* Controls */}
      <div className="mt-6 rounded-2xl border border-border bg-card p-6" data-testid="mines-controls">
        <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Mines</div>
        <div className="grid grid-cols-5 gap-2">
          {MINE_PRESETS.map((m) => {
            const c = MINE_COLORS[m];
            const on = mines === m;
            return (
              <button
                key={m}
                data-testid={`mines-preset-${m}`}
                onClick={() => !active && setMines(m)}
                disabled={active}
                style={on ? { background: c.grad, color: c.text, boxShadow: `0 0 18px rgba(${c.glow},0.5), inset 0 1px 0 rgba(255,255,255,0.35)` } : undefined}
                className={`rounded-xl py-3 text-center font-bold transition-[color,background-color,border-color,opacity,transform] disabled:opacity-40 ${
                  on ? "scale-[1.05]" : "bg-secondary hover:bg-secondary/70 text-foreground"
                }`}
              >
                <Bomb className="h-4 w-4 mx-auto mb-0.5" style={on ? undefined : { color: `rgb(${c.glow})` }} />
                <span className="text-sm">{m}</span>
              </button>
            );
          })}
        </div>
        <p className="mt-2 text-[11px] text-muted-foreground">More mines = fewer safe tiles but far bigger multipliers per gem.</p>

        <div className="mt-5">
          <label className="text-sm font-medium">Stake <span className="text-xs text-muted-foreground font-normal">(min ₹20 · max ₹100,000)</span></label>
          <Input type="number" min={20} max={100000} value={stake} disabled={active}
            onChange={(e) => setStake(e.target.value)} className="mt-1.5 disabled:opacity-60" data-testid="mines-stake-input" />
          <div className="mt-2 flex flex-wrap gap-1.5">
            {[20, 50, 100, 500, 1000, 2000, 5000, 10000].map((q) => {
              const special = q === 10000;
              return (
                <button key={q} onClick={() => !active && setStake(String(q))} disabled={active} data-testid={`mines-chip-${q}`}
                  className={special
                    ? "rounded-full px-3.5 py-1.5 text-sm font-extrabold text-black shadow-md ring-1 ring-yellow-200/70 hover:brightness-110 transition-[color,background-color,border-color,opacity,transform] disabled:opacity-40"
                    : "rounded-full bg-secondary px-3 py-1 text-xs font-semibold hover:bg-secondary/70 disabled:opacity-40"}
                  style={special ? { background: "linear-gradient(135deg,#fde68a,#f59e0b 55%,#b45309)" } : undefined}>
                  +{q}
                </button>
              );
            })}
          </div>
        </div>

        <Button
          className="mt-5 w-full h-12 font-extrabold text-base"
          onClick={start}
          disabled={busy || active}
          data-testid="mines-start-btn"
        >
          <Pickaxe className="h-5 w-5 mr-1.5" />
          {active ? "Round in progress…" : busy ? "Starting…" : "Start Digging"}
        </Button>

        {recent.length > 0 && (
          <div className="mt-4 flex items-center gap-2 overflow-x-auto no-scrollbar" data-testid="mines-recent">
            <RotateCw className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            {recent.map((r, i) => (
              <span key={i} className="shrink-0 rounded-full bg-primary/15 text-yellow-300 px-2.5 py-1 text-xs font-bold">
                {Number(r.mult).toFixed(2)}x
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TileFace({ state, isHit }) {
  if (state === "gem") {
    return (
      <span className="grid place-items-center h-full w-full rounded-xl"
        style={{
          background: "radial-gradient(circle at 50% 32%, #0e5c3a 0%, #073a25 100%)",
          boxShadow: "inset 0 0 14px rgba(0,0,0,0.4), 0 0 12px rgba(16,185,129,0.35)",
          border: "1px solid rgba(52,211,153,0.5)",
        }}>
        <Gem className="h-6 w-6 sm:h-7 sm:w-7 text-emerald-300 drop-shadow-[0_0_6px_rgba(52,211,153,0.9)]" />
      </span>
    );
  }
  if (state === "bomb") {
    return (
      <span className="grid place-items-center h-full w-full rounded-xl"
        style={{
          background: isHit
            ? "radial-gradient(circle at 50% 32%, #ef4444 0%, #7f1d1d 100%)"
            : "radial-gradient(circle at 50% 32%, #3a1414 0%, #1a0808 100%)",
          boxShadow: isHit ? "0 0 18px rgba(239,68,68,0.8)" : "inset 0 0 12px rgba(0,0,0,0.5)",
          border: isHit ? "1px solid rgba(248,113,113,0.9)" : "1px solid rgba(127,29,29,0.6)",
        }}>
        <Bomb className={`h-6 w-6 sm:h-7 sm:w-7 ${isHit ? "text-white" : "text-red-400/80"}`} />
      </span>
    );
  }
  if (state === "dim") {
    return <span className="h-full w-full rounded-xl bg-white/[0.03] ring-1 ring-white/5" />;
  }
  // hidden — polished gold-black tile
  return (
    <span className="grid place-items-center h-full w-full rounded-xl"
      style={{
        background: "linear-gradient(145deg, #2a3340 0%, #171e28 60%, #0d131b 100%)",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08), inset 0 -3px 8px rgba(0,0,0,0.5), 0 2px 5px rgba(0,0,0,0.4)",
        border: "1px solid rgba(217,181,69,0.28)",
      }}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: "rgba(217,181,69,0.45)" }} />
    </span>
  );
}
